import React from 'react';
import '../../styles/legal.css';

const TermsAndConditions = () => {
    return (
        <div className="legal-container">
            <div className="legal-header">
                <h1 className="legal-title">Terms & Conditions</h1>
                <p className="legal-meta">Last Updated: June 3, 2026</p>
            </div>

            <div className="legal-section">
                <h2>1. Acceptance of Terms</h2>
                <p>
                    Welcome to SIE QR Menu ("Service", "Platform"), a software-as-a-service (SaaS) application. 
                    By accessing or using our website, service, or administrative dashboard, you ("User", "Restaurant Operator", "Customer") 
                    agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use the Platform.
                </p>
                <div className="legal-alert">
                    <p>
                        SIE QR Menu is developed, maintained, and operated by <strong>Search is End!</strong>, which functions as the primary operating brand. 
                        All legal ownership, billing, and registrations are held under the legally registered proprietary firm <strong>Smart City Ajmer</strong> 
                        (GSTIN: 08ANVPS9429D1Z8).
                    </p>
                </div>
            </div>

            <div className="legal-section">
                <h2>2. Scope of Service</h2>
                <p>
                    SIE QR Menu acts strictly as a SaaS digital platform provider. We provide software tools that allow restaurant operators 
to create, customize, and manage digital QR code menus, process dine-in and delivery orders, view analytic insights, and access 
point-of-sale (POS) features. 
                </p>
                <p>
                    We do not prepare, cook, deliver, or handle any physical restaurant items, nor do we act as a party to any transactions 
                    between the restaurant operator and their end customers. The restaurant operator remains solely responsible for menu accuracy, 
                    food quality, safety compliance, pricing, order fulfillment, and collecting payments.
                </p>
            </div>

            <div className="legal-section">
                <h2>3. Account Registration & Security</h2>
                <p>
                    To use the Platform's administrative features, you must register for an account by providing accurate, current, and complete details. 
                    You are solely responsible for maintaining the confidentiality of your account credentials (username, passwords, tokens) and 
                    for all activities occurring under your account. You agree to notify us immediately of any unauthorized use or security breach.
                </p>
            </div>

            <div className="legal-section">
                <h2>4. Superadmin Access & Account Audits</h2>
                <p>
                    To ensure the integrity, security, and continuous operation of the Platform, you explicitly acknowledge and agree that 
                    authorized Superadministrators of Search is End! (Smart City Ajmer) retain full administrative access to your account data, 
                    including your menu structures, settings, order logs, and analytical data.
                </p>
                <p>
                    Superadministrators may access your account for the following purposes:
                </p>
                <ul>
                    <li>Providing requested technical support, onboarding assistance, and customer service.</li>
                    <li>Conducting system maintenance, database updates, security patches, and structural audits.</li>
                    <li>Verifying compliance with these Terms and Conditions, including usage limits and feature authorizations.</li>
                    <li>Troubleshooting payment, API, and subscription status anomalies.</li>
                </ul>
            </div>

            <div className="legal-section">
                <h2>5. Intellectual Property & Content Ownership</h2>
                <p>
                    <strong>Our Ownership:</strong> All rights, title, and interest in and to the Platform (including but not limited to software code, 
                    database schemas, layout designs, UI components, custom graphics, logo assets, and documentation) are and will remain the 
                    exclusive property of Smart City Ajmer and its licensors.
                </p>
                <p>
                    <strong>Your Content:</strong> The restaurant operator retains all proprietary rights and ownership of any content uploaded 
                    to the platform (including restaurant logos, food images, menu item descriptions, prices, and categories). By uploading this content, 
                    you grant Smart City Ajmer (Search is End!) a non-exclusive, worldwide, royalty-free license to host, store, cache, and display 
                    your content on your designated public menu URL for the duration of your service agreement.
                </p>
            </div>

            <div className="legal-section">
                <h2>6. Acceptable Use & User Conduct</h2>
                <p>
                    You agree not to engage in any of the following prohibited activities:
                </p>
                <ul>
                    <li>Violating any local, state, national, or international laws or regulations.</li>
                    <li>Uploading, posting, or transmitting any content that is defamatory, obscene, harassing, fraudulent, or infringes on third-party intellectual property.</li>
                    <li>Interfering with, disrupting, or attempting to compromise the security, integrity, or server performance of the Platform.</li>
                    <li>Introducing viruses, trojan horses, worms, logic bombs, or other malicious code to the servers or database.</li>
                    <li>Using automated crawlers, bots, or scraping tools to extract data from our platform without express permission.</li>
                    <li>Using the platform to conduct unauthorized, fraudulent, or deceptive commercial activities.</li>
                </ul>
            </div>

            <div className="legal-section">
                <h2>7. Products, Subscriptions & Payment Terms</h2>
                <p>
                    SIE QR Menu offers premium subscription tiers alongside its basic services. Detailed pricing, limits, and options are visible on 
                    the billing settings and landing page.
                </p>
                <ul>
                    <li><strong>Billing Cycles:</strong> Subscriptions are billed on a recurring monthly or yearly cycle.</li>
                    <li><strong>Non-Refundable:</strong> All subscription charges are strictly non-refundable once payment is completed. There are no refunds, credits, or prorated adjustments for partially used cycles.</li>
                    <li><strong>No Setup Fees:</strong> There are no setup fees, database installation charges, or hidden maintenance costs. You pay only the agreed subscription price.</li>
                    <li><strong>Taxes:</strong> All payments are subject to applicable taxes, including GST (Smart City Ajmer GSTIN: 08ANVPS9429D1Z8), which will be detailed on invoice settlements.</li>
                </ul>
            </div>

            <div className="legal-section">
                <h2>8. Sponsor & Advertising Network Terms</h2>
                <p>
                    By participating in the SIE QR Menu Advertising Network (Sponsor Portal), you agree to the following supplemental terms:
                </p>
                <ul>
                    <li><strong>KYC Verification:</strong> All sponsors must complete mandatory Know Your Customer (KYC) verification by providing valid GST, PAN, and business registration details. We reserve the right to reject any sponsor application without stating a reason.</li>
                    <li><strong>Ad Content:</strong> Sponsors are solely responsible for the content of their advertisements. Ads must not contain illegal, defamatory, deceptive, or restricted content.</li>
                    <li><strong>Pre-paid Wallet System:</strong> Ad campaigns are funded via a manual pre-paid wallet system. As stated in our Refund Policy, wallet deposits are non-refundable.</li>
                    <li><strong>Pricing:</strong> Cost Per Click (CPC) and Cost Per Mille/1000 Impressions (CPM) rates are determined dynamically by the platform Super Admin upon campaign approval.</li>
                </ul>
            </div>

            <div className="legal-section">
                <h2>9. Right to Suspend or Terminate</h2>
                <p>
                    We reserve the right, without liability or prior notice, to suspend, restrict, or terminate your access to the Platform (or any part thereof) 
                    if we believe you have violated these Terms and Conditions, engaged in fraudulent or illegal activity, or failed to pay subscription dues. 
                    Upon termination, your right to use the administrative panel, host your public menu, or run ad campaigns will cease immediately.
                </p>
            </div>

            <div className="legal-section">
                <h2>10. Limitation of Liability</h2>
                <p>
                    To the maximum extent permitted by applicable law, in no event shall Smart City Ajmer, Search is End!, or its team be liable 
                    for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits, revenues, customer orders, 
                    or data, arising out of or related to your use of or inability to use the Platform. The Service is provided on an "AS IS" and 
                    "AS AVAILABLE" basis without warranties of any kind.
                </p>
            </div>

            <div className="legal-section">
                <h2>11. Modifications to Terms</h2>
                <p>
                    We reserve the right to modify these Terms and Conditions at any time. When updates occur, we will post the revised terms on 
                    this page and update the "Last Updated" date. Continued use of the platform after updates have been posted constitutes 
                    your formal acceptance of the modified terms.
                </p>
            </div>

            <div className="legal-section">
                <h2>12. Governing Law & Dispute Resolution</h2>
                <p>
                    These Terms and Conditions and any disputes arising out of or relating to them shall be governed by, and construed in accordance with, 
                    the laws of India. 
                </p>
                <p>
                    Any legal action, suit, or proceeding arising out of or in connection with these Terms shall be instituted exclusively in the courts 
                    located in <strong>Ajmer, Rajasthan, India</strong>, and you hereby consent to the personal jurisdiction of such courts.
                </p>
            </div>

            <div className="legal-contact-box">
                <h3>Contact & Company Details</h3>
                <p><strong>Operating Brand:</strong> Search is End!</p>
                <p><strong>Registered Proprietorship:</strong> Smart City Ajmer</p>
                <p><strong>GST Number:</strong> 08ANVPS9429D1Z8</p>
                <p><strong>Support & Legal Email:</strong> <a href="mailto:searchisend@smartcityajmer.com">searchisend@smartcityajmer.com</a></p>
                <p><strong>Mailing Address:</strong> Registered address of Smart City Ajmer (Ajmer, Rajasthan, India).</p>
                <p style={{ fontSize: '0.85em', color: '#64748b', marginTop: '15px' }}>
                    <em>Disclaimer: If the registered physical address of Smart City Ajmer is updated, this mailing address is deemed updated in tandem. 
                    For the latest active registered address, please verify the registration certificate on the official Smart City Ajmer website.</em>
                </p>
            </div>
        </div>
    );
};

export default TermsAndConditions;
